import { create } from "zustand"
import type { BooruPost } from "./booru-api"

interface NavigationEntry {
  type: 'main' | 'board' | 'album'
  boardId?: string
  album?: BooruPost // Store the full album object for proper restoration
  categoryName?: string // Store category context for Doujins navigation (e.g., "featured", "doujins", "artists/otochichi")
  scrollPosition: number
  timestamp: number
}

/**
 * Navigation Store - Manages navigation history for nested views
 * 
 * Navigation Flow for Doujins:
 * 1. Main View (categories: Featured, Recent, etc.)
 * 2. Category View (album view with isCategory=true, shows galleries)
 * 3. Gallery View (album view with isCategory=false, shows pages)
 * 
 * Back Button Behavior:
 * - From Gallery → Category (NOT Main!)
 * - From Category → Main
 * 
 * The key is that when navigating from Category to Gallery, we save the
 * Category as an 'album' type in the navigation stack, so pressing back
 * restores the Category view instead of going to Main.
 * 
 * CRITICAL FIX - Double Navigation Prevention:
 * ============================================
 * Problem: When UI back button was clicked, navigateBack() called window.history.back(),
 * which triggered the popstate event, which then called navigateBack(false) again.
 * This caused double navigation: Layer 3 → Layer 1 → Layer 2 (with delay).
 * 
 * Solution: Use window.history.replaceState() instead of window.history.back() when
 * the UI back button is pressed (updateHistory=true). This updates the browser URL
 * to match the new state WITHOUT triggering a popstate event.
 * 
 * Flow:
 * - UI back button → navigateBack(true) → replaceState (no popstate) → single navigation ✅
 * - Browser back button → popstate event → navigateBack(false) → no history update ✅
 * 
 * This pattern is essential for complex multi-layer navigation systems where you need
 * to maintain both internal navigation state and browser history synchronization without
 * causing duplicate navigation events.
 * 
 * Date: 2025-01-10
 * Related Issue: Doujins navigation - back button going to wrong layer with delay
 */

interface NavigationStore {
  currentView: 'main' | 'board' | 'album'
  currentBoard: string | null
  currentAlbum: BooruPost | null
  navigationStack: NavigationEntry[]
  scrollPositions: Map<string, number>
  
  navigateToBoard: (boardId: string) => void
  navigateToAlbum: (album: BooruPost) => void
  navigateBack: (updateHistory?: boolean) => void // Add optional parameter to control history update
  saveScrollPosition: (key: string, position: number) => void
  getScrollPosition: (key: string) => number
  clearNavigation: () => void
}

export const useNavigationStore = create<NavigationStore>((set, get) => ({
  currentView: 'main',
  currentBoard: null,
  currentAlbum: null,
  navigationStack: [],
  scrollPositions: new Map(),
  
  navigateToBoard: (boardId: string) => {
    const { scrollPositions } = get()
    set({
      currentView: 'board',
      currentBoard: boardId,
      currentAlbum: null,
      navigationStack: [
        ...get().navigationStack,
        {
          type: 'main',
          scrollPosition: scrollPositions.get('main') || 0,
          timestamp: Date.now(),
        },
      ],
    })
    
    // Update browser history
    if (typeof window !== 'undefined') {
      window.history.pushState(
        { type: 'board', boardId },
        '',
        `?board=${boardId}`
      )
    }
  },
  
  navigateToAlbum: (album: BooruPost) => {
    const { scrollPositions, currentView, currentBoard, currentAlbum, navigationStack } = get()
    
    console.log('╔════════════════════════════════════════════════════════════════════════════╗')
    console.log('║                        NAVIGATE TO ALBUM - START                           ║')
    console.log('╚════════════════════════════════════════════════════════════════════════════╝')
    console.log('[NavigationStore] 📍 Current State:')
    console.log('  • currentView:', currentView)
    console.log('  • currentBoard:', currentBoard)
    console.log('  • navigationStack.length:', navigationStack.length)
    console.log('')
    console.log('[NavigationStore] 📦 Current Album (FROM):')
    console.log('  • title:', currentAlbum?.title)
    console.log('  • id:', currentAlbum?.id)
    console.log('  • site:', currentAlbum?.site)
    console.log('  • isCategory:', currentAlbum?.rawPost?.isCategory)
    console.log('  • galleryUrl:', currentAlbum?.rawPost?.galleryUrl)
    console.log('')
    console.log('[NavigationStore] 🎯 New Album (TO):')
    console.log('  • title:', album.title)
    console.log('  • id:', album.id)
    console.log('  • site:', album.site)
    console.log('  • isCategory:', album.rawPost?.isCategory)
    console.log('  • galleryUrl:', album.rawPost?.galleryUrl)
    console.log('')
    
    // Determine if we should save the current state to the navigation stack
    // We always save when navigating to a new album, but the type depends on current state
    let previousView: 'main' | 'board' | 'album'
    let categoryName: string | undefined
    
    if (currentView === 'album' && currentAlbum) {
      console.log('[NavigationStore] 🔍 Analyzing navigation from album to album...')
      
      // We're navigating from one album to another
      // Check if the current album is a category (doujins category view)
      const isCurrentCategory = currentAlbum.site === 'doujins' && 
                                (currentAlbum.rawPost?.isCategory || 
                                 currentAlbum.rawPost?.galleryUrl?.startsWith('category:'))
      
      console.log('  • Current album is doujins?', currentAlbum.site === 'doujins')
      console.log('  • Current album has isCategory flag?', currentAlbum.rawPost?.isCategory)
      console.log('  • Current album galleryUrl starts with "category:"?', currentAlbum.rawPost?.galleryUrl?.startsWith('category:'))
      console.log('  • ✅ isCurrentCategory:', isCurrentCategory)
      console.log('')
      
      if (isCurrentCategory) {
        // Navigating from a category to a gallery - save as 'album' so we return to the category
        previousView = 'album'
        
        // Extract category name for breadcrumb display
        const galleryUrl = currentAlbum.rawPost?.galleryUrl
        if (typeof galleryUrl === 'string' && galleryUrl.startsWith('category:')) {
          categoryName = galleryUrl.replace('category:', '')
          console.log('[NavigationStore] ✅ CATEGORY NAVIGATION DETECTED!')
          console.log('  • Category name:', categoryName)
          console.log('  • Will save current album to stack for back navigation')
          console.log('  • previousView set to: "album"')
        }
      } else {
        // Navigating from a regular gallery to another gallery
        // This should go back to main view, not stay in album view
        console.log('[NavigationStore] ⚠️  Gallery-to-gallery navigation - should go back to main')
        console.log('  • Current album is not a category, treating as main view navigation')
        console.log('  • This will clear the navigation stack and go back to main')
        
        // Clear the navigation stack since we're doing a fresh navigation from main
        set({
          currentView: 'album',
          currentAlbum: album,
          navigationStack: [{
            type: 'main',
            scrollPosition: scrollPositions.get('main') || 0,
            timestamp: Date.now(),
          }],
        })
        
        // Update browser history
        if (typeof window !== 'undefined') {
          window.history.pushState(
            { type: 'album', albumId: album.id, albumSite: album.site },
            '',
            `?album=${album.site}:${album.id}`
          )
          console.log('[NavigationStore] 🌐 Browser history updated (fresh navigation)')
        }
        
        console.log('[NavigationStore] ✅ Fresh navigation completed - stack reset to main view')
        console.log('╔════════════════════════════════════════════════════════════════════════════╗')
        console.log('║                         NAVIGATE TO ALBUM - END                            ║')
        console.log('╚════════════════════════════════════════════════════════════════════════════╝')
        console.log('')
        return
      }
    } else if (currentView === 'board') {
      previousView = 'board'
      console.log('[NavigationStore] 📋 Navigating from board view')
      console.log('  • previousView set to: "board"')
    } else {
      // Navigating from main view
      previousView = 'main'
      console.log('[NavigationStore] 🏠 Navigating from main view')
      console.log('  • previousView set to: "main"')
    }
    
    console.log('')
    console.log('[NavigationStore] 💾 Creating navigation entry:')
    const newEntry = {
      type: previousView,
      boardId: currentBoard || undefined,
      album: currentAlbum || undefined,
      categoryName: categoryName,
      scrollPosition: scrollPositions.get(previousView === 'album' ? 'album' : previousView === 'board' ? 'board' : 'main') || 0,
      timestamp: Date.now(),
    }
    console.log('  • type:', newEntry.type)
    console.log('  • boardId:', newEntry.boardId)
    console.log('  • album.title:', newEntry.album?.title)
    console.log('  • album.id:', newEntry.album?.id)
    console.log('  • categoryName:', newEntry.categoryName)
    console.log('  • scrollPosition:', newEntry.scrollPosition)
    console.log('')
    
    set({
      currentView: 'album',
      currentAlbum: album,
      navigationStack: [
        ...get().navigationStack,
        newEntry,
      ],
    })
    
    const updatedStack = get().navigationStack
    console.log('[NavigationStore] ✅ State updated!')
    console.log('  • New currentView:', get().currentView)
    console.log('  • New currentAlbum:', get().currentAlbum?.title)
    console.log('  • Navigation stack length:', updatedStack.length)
    console.log('')
    console.log('[NavigationStore] 📚 Full Navigation Stack:')
    updatedStack.forEach((entry, index) => {
      console.log(`  [${index}] type: ${entry.type}, album: ${entry.album?.title || 'none'}, category: ${entry.categoryName || 'none'}`)
    })
    console.log('')
    
    // Update browser history
    if (typeof window !== 'undefined') {
      window.history.pushState(
        { type: 'album', albumId: album.id, albumSite: album.site, boardId: currentBoard, categoryName },
        '',
        `?album=${album.site}:${album.id}`
      )
      console.log('[NavigationStore] 🌐 Browser history updated')
    }
    
    console.log('╔════════════════════════════════════════════════════════════════════════════╗')
    console.log('║                         NAVIGATE TO ALBUM - END                            ║')
    console.log('╚════════════════════════════════════════════════════════════════════════════╝')
    console.log('')
  },
  
  navigateBack: (updateHistory = true) => {
    const { navigationStack } = get()
    
    console.log('╔════════════════════════════════════════════════════════════════════════════╗')
    console.log('║                          NAVIGATE BACK - START                             ║')
    console.log('╚════════════════════════════════════════════════════════════════════════════╝')
    console.log('[NavigationStore] 🔙 Navigate back called')
    console.log('  • updateHistory:', updateHistory)
    console.log('  • Stack length BEFORE pop:', navigationStack.length)
    console.log('')
    
    if (navigationStack.length === 0) {
      console.log('[NavigationStore] ⚠️  WARNING: Navigation stack is EMPTY!')
      console.log('  • Cannot navigate back - staying on current view')
      console.log('  • Current view:', get().currentView)
      console.log('  • Current album:', get().currentAlbum?.title)
      console.log('╚════════════════════════════════════════════════════════════════════════════╝')
      console.log('')
      return
    }
    
    const newStack = [...navigationStack]
    const previousEntry = newStack.pop()
    
    console.log('[NavigationStore] 📦 Popped entry from stack:')
    console.log('  • type:', previousEntry?.type)
    console.log('  • boardId:', previousEntry?.boardId)
    console.log('  • album.title:', previousEntry?.album?.title)
    console.log('  • album.id:', previousEntry?.album?.id)
    console.log('  • album.site:', previousEntry?.album?.site)
    console.log('  • album.isCategory:', previousEntry?.album?.rawPost?.isCategory)
    console.log('  • album.galleryUrl:', previousEntry?.album?.rawPost?.galleryUrl)
    console.log('  • categoryName:', previousEntry?.categoryName)
    console.log('  • scrollPosition:', previousEntry?.scrollPosition)
    console.log('  • timestamp:', previousEntry?.timestamp ? new Date(previousEntry.timestamp).toISOString() : 'none')
    console.log('')
    console.log('[NavigationStore] 📚 Remaining stack after pop:')
    newStack.forEach((entry, index) => {
      console.log(`  [${index}] type: ${entry.type}, album: ${entry.album?.title || 'none'}, category: ${entry.categoryName || 'none'}`)
    })
    console.log('')
    
    if (previousEntry?.type === 'album') {
      // Restore the previous album view (e.g., returning to a category)
      console.log('[NavigationStore] ✅ Restoring ALBUM view')
      console.log('  • Album to restore:', previousEntry.album?.title)
      console.log('  • Album ID:', previousEntry.album?.id)
      console.log('  • Is category?:', previousEntry.album?.rawPost?.isCategory)
      console.log('  • Gallery URL:', previousEntry.album?.rawPost?.galleryUrl)
      console.log('')
      
      set({
        currentView: 'album',
        currentBoard: previousEntry.boardId || null,
        currentAlbum: previousEntry.album || null, // Restore the album object
        navigationStack: newStack,
      })
    } else if (previousEntry?.type === 'board') {
      console.log('[NavigationStore] ✅ Restoring BOARD view')
      console.log('  • Board ID:', previousEntry.boardId)
      console.log('')
      
      set({
        currentView: 'board',
        currentBoard: previousEntry.boardId || null,
        currentAlbum: null,
        navigationStack: newStack,
      })
    } else {
      console.log('[NavigationStore] ✅ Restoring MAIN view')
      console.log('  • Clearing all album/board state')
      console.log('')
      
      set({
        currentView: 'main',
        currentBoard: null,
        currentAlbum: null,
        navigationStack: newStack,
      })
    }
    
    const finalState = get()
    console.log('[NavigationStore] 🎯 Final state after navigation:')
    console.log('  • currentView:', finalState.currentView)
    console.log('  • currentBoard:', finalState.currentBoard)
    console.log('  • currentAlbum.title:', finalState.currentAlbum?.title)
    console.log('  • currentAlbum.id:', finalState.currentAlbum?.id)
    console.log('  • currentAlbum.isCategory:', finalState.currentAlbum?.rawPost?.isCategory)
    console.log('  • navigationStack.length:', finalState.navigationStack.length)
    console.log('')
    
    // Update browser history to match our state
    if (updateHistory && typeof window !== 'undefined') {
      console.log('[NavigationStore] 🌐 Updating browser history (UI back button pressed)')
      
      // When the UI back button is pressed, we need to update the browser URL
      // to match our new state WITHOUT triggering a popstate event.
      // We use replaceState instead of history.back() to avoid double navigation.
      
      let url = '/'
      let historyState: any = { type: finalState.currentView }
      
      if (finalState.currentView === 'album' && finalState.currentAlbum) {
        url = `?album=${finalState.currentAlbum.site}:${finalState.currentAlbum.id}`
        historyState.albumId = finalState.currentAlbum.id
        historyState.albumSite = finalState.currentAlbum.site
      } else if (finalState.currentView === 'board' && finalState.currentBoard) {
        url = `?board=${finalState.currentBoard}`
        historyState.boardId = finalState.currentBoard
      }
      
      // Replace current history entry to update URL without navigation
      window.history.replaceState(historyState, '', url)
      console.log('[NavigationStore] 🌐 Browser URL updated to:', url)
      console.log('[NavigationStore] ℹ️  Using replaceState to avoid triggering popstate')
    } else if (!updateHistory) {
      console.log('[NavigationStore] ⏭️  Skipping browser history update (triggered by popstate)')
    }
    
    console.log('╔════════════════════════════════════════════════════════════════════════════╗')
    console.log('║                           NAVIGATE BACK - END                              ║')
    console.log('╚════════════════════════════════════════════════════════════════════════════╝')
    console.log('')
  },
  
  saveScrollPosition: (key: string, position: number) => {
    const { scrollPositions } = get()
    const newPositions = new Map(scrollPositions)
    newPositions.set(key, position)
    set({ scrollPositions: newPositions })
  },
  
  getScrollPosition: (key: string) => {
    return get().scrollPositions.get(key) || 0
  },
  
  clearNavigation: () => {
    set({
      currentView: 'main',
      currentBoard: null,
      currentAlbum: null,
      navigationStack: [],
      scrollPositions: new Map(),
    })
  },
}))
